import React, { useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { getDepartmentMembersCall } from '@/departments/state/api';
import { ChooseDepartmentEmployees } from './ChooseDepartmentEmployees';
import { ChooseTeamsOrGuest } from './ChooseTeamsOrGuest';
import { ConfirmDepartmentEmployees } from './ConfirmDepartmentEmployees';
import { DelegateGuest } from './DelegateGuest';

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
};

export enum AddDelegateStep {
  CHOOSE_DEPARTMENT_OR_GUEST = 'ChooseTeamsOrGuest',
  ADD_DELEGATE_EMPLOYEES = 'ChooseDepartmentEmployees',
  CONFIRM_DELEGATE_EMPLOYEES = 'ConfirmDepartmentEmployees',
  ADD_DELEGATE_GUEST = 'DelegateGuest',
}

export const AddDelegate = ({ isOpen, closeDialog }: Props) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.dialog');
  const [currentStep, setCurrentStep] = useState<AddDelegateStep>(
    AddDelegateStep.CHOOSE_DEPARTMENT_OR_GUEST,
  );
  const [department, setDepartment] = useState<{ id: string; name?: string } | undefined>(
    undefined,
  );
  const [selectedEmployer, setSelectedEmployer] = useState<
    ReturnType<typeof getDepartmentMembersCall.hook>['allEmployees']
  >([]);

  const displayDepartmentName =
    department?.name?.toLowerCase() === 'unknown'
      ? t('chooseTeamsOrGuest.unknownDepartment')
      : department?.name || '';

  const resetAndClose = () => {
    setCurrentStep(AddDelegateStep.CHOOSE_DEPARTMENT_OR_GUEST);
    setDepartment(undefined);
    setSelectedEmployer([]);
    closeDialog();
  };

  return (
    <Dialog
      maxWidth='800px'
      name='addDelegate'
      open={isOpen}
      title={undefined}
      onClose={resetAndClose}
      closeButton
    >
      <Box width='100%'>
        <Box display='flex' flexDirection='column'>
          <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
            {currentStep === AddDelegateStep.CONFIRM_DELEGATE_EMPLOYEES
              ? t('delegateEmployees.confirm.title')
              : t('title')}
          </Typography>
        </Box>

        <Box mb='xs' mt='m' width='100%'>
          {currentStep === AddDelegateStep.CHOOSE_DEPARTMENT_OR_GUEST && (
            <ChooseTeamsOrGuest
              onDepartmentChoose={selectedDepartment => {
                setDepartment({
                  id: selectedDepartment.id,
                  name: selectedDepartment.name,
                });
                setCurrentStep(AddDelegateStep.ADD_DELEGATE_EMPLOYEES);
              }}
              onGuestChoose={() => setCurrentStep(AddDelegateStep.ADD_DELEGATE_GUEST)}
            />
          )}
          {currentStep === AddDelegateStep.ADD_DELEGATE_EMPLOYEES && (
            <ChooseDepartmentEmployees
              departmentId={department?.id}
              departmentName={displayDepartmentName}
              selectedEmployer={selectedEmployer}
              setSelectedEmployer={setSelectedEmployer}
              goBack={() => setCurrentStep(AddDelegateStep.CHOOSE_DEPARTMENT_OR_GUEST)}
              onNext={() => setCurrentStep(AddDelegateStep.CONFIRM_DELEGATE_EMPLOYEES)}
            />
          )}
          {currentStep === AddDelegateStep.CONFIRM_DELEGATE_EMPLOYEES && (
            <ConfirmDepartmentEmployees
              selectedEmployees={selectedEmployer}
              departmentName={displayDepartmentName}
              goBack={() => setCurrentStep(AddDelegateStep.ADD_DELEGATE_EMPLOYEES)}
              onConfirm={resetAndClose}
            />
          )}
          {currentStep === AddDelegateStep.ADD_DELEGATE_GUEST && (
            <DelegateGuest closeDialog={resetAndClose} />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
